import React, { useCallback, useMemo } from 'react'
import {
  Box,
  Container,
  Grid,
  IconButton,
  Typography,
  useTheme,
} from '@material-ui/core'
import { MetaTags } from 'react-meta-tags'
import { makeStyles } from '@material-ui/core/styles'

import { colors } from 'theme'
import { navigateTo } from 'utils/helpers'
import canonicalUrl from 'utils/canonicalUrl'

import Breadcrumbs from 'staticPages/Breadcrumbs'
import { capitalize } from 'lodash'
import { ReactComponent as BackIcon } from './icon-back.svg'

const Header = ({ header, subheader, breadcrumbs }) => {
  const theme = useTheme()
  const classes = useStyles()

  const breadcrumbsStructuredData = useMemo(() => {
    if (!breadcrumbs?.length) {
      return null
    }

    const items = [
      ...breadcrumbs,
      { label: header, link: window.location.pathname },
    ]

    const itemListElement = items.map(({ label, link }, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: label,
      item: canonicalUrl({ pathname: link }),
    }))

    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement,
    }
  }, [breadcrumbs, header])

  const goBack = useCallback(() => {
    if (window.history.length) {
      window.history.back()
    } else {
      navigateTo('/')
    }
  }, [])

  if (!header) return null

  let pathObjects
  if (breadcrumbs) {
    pathObjects = breadcrumbs.map(breadcrumb => ({
      href: breadcrumb.link,
      title: breadcrumb.label,
    }))

    pathObjects.push({ title: capitalize(header) })
  }

  return (
    <Box
      bgcolor={colors.aqua700}
      alignSelf="stretch"
      css={{
        padding: theme.spacing(2, 0),
        marginTop: theme.spacing(-2),
      }}
    >
      <Container
        css={{
          maxWidth: 'min(960px, calc(100% - 24px))',
          padding: 0,
        }}
      >
        <Grid
          container
          direction="column"
          justify="center"
          style={{
            padding: theme.spacing(5, 0),
          }}
        >
          {!!breadcrumbs?.length && (
            <>
              <Breadcrumbs
                className={classes.breadcrumbs}
                pathObjects={pathObjects}
              />

              <MetaTags>
                <script type="application/ld+json">
                  {JSON.stringify(breadcrumbsStructuredData, null, 2)}
                </script>
              </MetaTags>
            </>
          )}

          <Grid
            alignItems="center"
            container
            style={{ marginTop: theme.spacing(2) }}
          >
            <IconButton onClick={goBack} style={{ padding: 0 }}>
              <BackIcon />
            </IconButton>

            <Typography
              variant="h2"
              component="h1"
              color="secondary"
              style={{
                marginLeft: theme.spacing(2),
                fontSize: 46,
                flex: 1,
                textTransform: 'capitalize',
                fontWeight: 500,
                [theme.breakpoints.down('sm')]: {
                  fontSize: 32,
                },
              }}
            >
              {header}
            </Typography>
          </Grid>
          {subheader && (
            <Typography
              variant="h7"
              component="h2"
              color="secondary"
              style={{
                marginTop: theme.spacing(2),
                [theme.breakpoints.down('sm')]: {
                  marginTop: theme.spacing(1),
                },
              }}
            >
              {subheader}
            </Typography>
          )}
        </Grid>
      </Container>
    </Box>
  )
}

const useStyles = makeStyles(() => ({
  breadcrumbs: {
    color: colors.white,
    '& a': {
      color: colors.white,
    },
  },
}))

export default Header
